import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import parts from 'redux/parts';
import { DEFAULT_PAGE_SIZE } from 'utils/pagination';

import BrandLogo from 'components/atoms/BrandLogo';
import ModelImages from 'components/atoms/ModelImages';
import Table from 'components/molecules/Table';
import TableSearch from 'components/molecules/TableSearch';
import PurchaseButton from 'components/molecules/PurchaseButton';
import CartModal from 'components/molecules/CartModal';
import ScrapeGuard from 'components/molecules/ScrapeGuard';
import useCart from 'hooks/useCart';
import usePagination from 'hooks/usePagination';

import newIcon from 'assets/icons/new.png';

import './index.scss';

const Promotions = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation('common');
  const promotionsParts = useSelector(parts.selectors.selectAll);
  const loading = useSelector(parts.selectors.getLoadingState);
  const { total_entries } = useSelector(parts.selectors.getMetaData);
  const { onSubmit, updateRecords, onModalClose, cartItems, cartLoader, cartAlert, cartTotal } = useCart();
  const { pagination, onPaginationChange, reset } = usePagination();
  const [name, setName] = useState('');

  useEffect(() => {
    dispatch(parts.thunks.fetchPromotions({ params: { page: 1, per_page: DEFAULT_PAGE_SIZE } }));
  }, [dispatch]);

  const onChange = (nextPagination) => {
    const { current, pageSize } = onPaginationChange(nextPagination);
    dispatch(
      parts.thunks.fetchPromotions({
        params: { name: name || undefined, page: current, per_page: pageSize },
        selectedItems: cartItems,
      }),
    );
    window.scrollTo(0, 0);
  };

  /* Paged from the API, so the name has to go with the request - a browser-side
     filter would only search the page on screen. A new term starts at page one. */
  const onSearchName = (value) => {
    setName(value);
    const { current, pageSize } = reset();
    dispatch(parts.thunks.fetchPromotions({ params: { name: value || undefined, page: current, per_page: pageSize } }));
  };

  const columns = [
    {
      title: 'product-name',
      dataIndex: 'arabic_name',
      render: (text, record) => (
        <React.Fragment>
          {text} {record.newly_updated && <img src={newIcon} alt="New" />}
        </React.Fragment>
      ),
      width: '20%',
    },
    {
      title: 'model',
      dataIndex: 'show_model_names',
      // Each model the part fits, as a previewable thumbnail above its name.
      render: (value, record) => <ModelImages models={(value && record.models) || []} />,
    },
    {
      title: 'brand',
      dataIndex: 'brand_image',
      className: 'brand-cell',
      render: (value, record) => <BrandLogo src={value} name={record.brand_name} />,
    },
    {
      title: 'country',
      dataIndex: 'country_name',
    },
    {
      title: 'remark',
      dataIndex: 'remark',
    },
    {
      title: 'old-price',
      dataIndex: 'price_and_promotion',
      render: (value) => <s>{Math.floor(value?.price)?.toLocaleString()}</s>,
    },
    {
      title: 'new-price',
      dataIndex: 'price_and_promotion',
      render: (value) => Math.floor(value?.promotion)?.toLocaleString(),
    },
    {
      title: 'quantity',
      dataIndex: ['in_cart', 'quantity'],
      isEditable: true,
      type: 'number',
    },
    {
      title: 'total',
      dataIndex: 'total',
      render: (_, record) => {
        return (
          (
            Math.floor(record.price_and_promotion?.promotion || record.price) * record.in_cart.quantity
          )?.toLocaleString() || 0
        );
      },
    },
    {
      title: 'images',
      dataIndex: 'images',
      type: 'images',
      width: '20%',
    },
    {
      title: 'code',
      dataIndex: 'code',
    },
  ];

  return (
    <div title={t('promotions')}>
      <CartModal alert={cartAlert} onCancel={onModalClose} total={cartTotal} />
      <PurchaseButton buttonTitle={t('add-to-cart')} sideLabel={t('add-to-cart-side-label')} onClick={onSubmit} />
      <TableSearch className="table-search--block" defaultValue={name} onSearch={onSearchName} />
      <ScrapeGuard>
        <Table
          columns={columns}
          dataSource={promotionsParts}
          updateDataSource={updateRecords}
          loading={loading || cartLoader}
          rowKey="id"
          localePath="table-fields"
          pagination={{ ...pagination, total: total_entries }}
          onChange={onChange}
        />
      </ScrapeGuard>
    </div>
  );
};

export default Promotions;
